import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Boxes, Inbox, Plus } from 'lucide-react';
import {
  Badge,
  Button,
  EmptyState,
  PageHeader,
  Select,
  Spinner,
  Table,
  type SelectOption,
  type TableColumn,
} from '../../ui';
import { useActiveRoom } from '../../features/rooms/RoomContext';
import { useTasks } from '../../features/tasks/useTasks';
import { useGoals } from '../../features/goals/useGoals';
import { useServices } from '../../features/services/useServices';
import {
  collectOwners,
  formatTaskEstimate,
  isEstimated,
  isSubtask,
  TASK_TYPES,
} from '../../features/tasks/taskModel';
import type { Task } from '../../features/tasks/types';
import { QuickCreateTaskModal } from '../Tasks/QuickCreateTaskModal';
import { useSprints } from '../../features/sprintboard/useSprints';

type EstimateFilter = '' | 'yes' | 'no';

interface BacklogFilterState {
  owner: string;
  type: string;
  goal: string;
  service: string;
  estimate: EstimateFilter;
}

const EMPTY_FILTER: BacklogFilterState = {
  owner: '',
  type: '',
  goal: '',
  service: '',
  estimate: '',
};

/**
 * Backlog — the room's unsprinted work: every non-deleted task with no sprint
 * assignment, shown as a simple Task / Owner / Estimate table. Lightweight
 * filters (Owner, Type, Goal, Service, Estimated / Not Estimated) narrow the
 * list; an inline "Create Task" adds straight to the backlog. Rows open the
 * existing task detail page. Sprint assignment happens on the planning surface.
 */
export function BacklogPage() {
  const navigate = useNavigate();
  const { activeRoom, activeRoomId } = useActiveRoom();
  const { tasks, loading, error, create, researchTask, refresh } = useTasks();
  const { currentSprint } = useSprints();
  const { goals } = useGoals();
  const { services } = useServices();

  const [filter, setFilter] = useState<BacklogFilterState>(EMPTY_FILTER);
  const [quickOpen, setQuickOpen] = useState(false);

  // Backlog = unsprinted, TOP-LEVEL work only (subtasks live under their parent).
  const backlog = useMemo(
    () => tasks.filter((t) => !t.sprintId && !isSubtask(t)),
    [tasks],
  );

  const goalTitles = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of goals) m.set(g.id, g.title);
    return m;
  }, [goals]);
  const serviceNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of services) m.set(s.id, s.name);
    return m;
  }, [services]);

  const ownerOptions = useMemo<SelectOption[]>(
    () => collectOwners(backlog).map((o) => ({ value: o, label: o })),
    [backlog],
  );
  const typeOptions = useMemo<SelectOption[]>(
    () => TASK_TYPES.map((t) => ({ value: t, label: t })),
    [],
  );
  // Only offer Goal/Service filter options that actually appear in the backlog.
  const goalOptions = useMemo<SelectOption[]>(() => {
    const ids = new Set(backlog.map((t) => t.goalId).filter(Boolean) as string[]);
    return Array.from(ids).map((id) => ({
      value: id,
      label: goalTitles.get(id) ?? id,
    }));
  }, [backlog, goalTitles]);
  const serviceOptions = useMemo<SelectOption[]>(() => {
    const ids = new Set(
      backlog.map((t) => t.serviceId).filter(Boolean) as string[],
    );
    return Array.from(ids).map((id) => ({
      value: id,
      label: serviceNames.get(id) ?? id,
    }));
  }, [backlog, serviceNames]);
  const estimateOptions: SelectOption[] = [
    { value: 'yes', label: 'Estimated' },
    { value: 'no', label: 'Not estimated' },
  ];

  const filtered = useMemo(() => {
    return backlog.filter((t) => {
      if (filter.owner && (t.owner ?? '') !== filter.owner) return false;
      if (filter.type && (t.type ?? 'Task') !== filter.type) return false;
      if (filter.goal && (t.goalId ?? '') !== filter.goal) return false;
      if (filter.service && (t.serviceId ?? '') !== filter.service) return false;
      if (filter.estimate === 'yes' && !isEstimated(t)) return false;
      if (filter.estimate === 'no' && isEstimated(t)) return false;
      return true;
    });
  }, [backlog, filter]);

  const filtersActive =
    !!filter.owner ||
    !!filter.type ||
    !!filter.goal ||
    !!filter.service ||
    !!filter.estimate;

  const columns: TableColumn<Task>[] = [
    {
      key: 'task',
      header: 'Task',
      render: (t) => (
        <div className="flex items-center gap-2">
          <Badge tone="neutral">{t.type ?? 'Task'}</Badge>
          <span className="font-medium text-zinc-100">{t.title}</span>
          {t.blocked && <Badge tone="danger" dot>Blocked</Badge>}
        </div>
      ),
    },
    {
      key: 'owner',
      header: 'Owner',
      render: (t) =>
        t.owner ? (
          <span className="text-zinc-300">{t.owner}</span>
        ) : (
          <span className="text-zinc-600">Unassigned</span>
        ),
    },
    {
      key: 'estimate',
      header: 'Estimate',
      align: 'right',
      render: (t) => {
        const label = formatTaskEstimate(t);
        const tone =
          label === 'Not Estimated'
            ? 'neutral'
            : label === 'Spike'
              ? 'info'
              : 'accent';
        return <Badge tone={tone}>{label}</Badge>;
      },
    },
  ];

  // --- No active room: prompt selection ------------------------------------
  if (!activeRoomId) {
    return (
      <div>
        <PageHeader
          icon={<Inbox className="h-5 w-5" />}
          title="Backlog"
          description="Unsprinted work for a room."
        />
        <EmptyState
          icon={<Boxes className="h-6 w-6" />}
          title="No active room"
          description="The backlog lives inside a room. Select or create a room to start planning work."
          action={
            <Button
              leftIcon={<Boxes className="h-4 w-4" />}
              onClick={() => navigate('/rooms')}
            >
              Go to Rooms
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        icon={<Inbox className="h-5 w-5" />}
        title="Backlog"
        description={
          <span className="inline-flex items-center gap-1.5">
            Unsprinted work in
            <Badge tone="accent">{activeRoom?.name ?? 'active room'}</Badge>
          </span>
        }
        actions={
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setQuickOpen(true)}>
            Create Task
          </Button>
        }
      />

      {/* Filters */}
      <div className="mb-5 flex flex-wrap items-end gap-3">
        <div className="w-40">
          <Select
            label="Owner"
            placeholder="All owners"
            value={filter.owner}
            options={ownerOptions}
            onChange={(e) => setFilter((f) => ({ ...f, owner: e.target.value }))}
          />
        </div>
        <div className="w-36">
          <Select
            label="Type"
            placeholder="All types"
            value={filter.type}
            options={typeOptions}
            onChange={(e) => setFilter((f) => ({ ...f, type: e.target.value }))}
          />
        </div>
        <div className="w-44">
          <Select
            label="Goal"
            placeholder="All goals"
            value={filter.goal}
            options={goalOptions}
            onChange={(e) => setFilter((f) => ({ ...f, goal: e.target.value }))}
          />
        </div>
        <div className="w-44">
          <Select
            label="Service"
            placeholder="All services"
            value={filter.service}
            options={serviceOptions}
            onChange={(e) => setFilter((f) => ({ ...f, service: e.target.value }))}
          />
        </div>
        <div className="w-44">
          <Select
            label="Estimate"
            placeholder="Any"
            value={filter.estimate}
            options={estimateOptions}
            onChange={(e) =>
              setFilter((f) => ({
                ...f,
                estimate: e.target.value as EstimateFilter,
              }))
            }
          />
        </div>
        {filtersActive && (
          <Button variant="ghost" size="sm" onClick={() => setFilter(EMPTY_FILTER)}>
            Clear filters
          </Button>
        )}
        <span className="ml-auto self-center text-xs text-zinc-500">
          {filtered.length} of {backlog.length}{' '}
          {backlog.length === 1 ? 'task' : 'tasks'}
        </span>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Spinner label="Loading backlog" />
        </div>
      ) : error ? (
        <EmptyState
          icon={<AlertTriangle className="h-6 w-6" />}
          title="Couldn't load the backlog"
          description={error}
        />
      ) : backlog.length === 0 ? (
        <EmptyState
          icon={<Inbox className="h-6 w-6" />}
          title="Backlog is empty"
          description="Every task is either sprinted or there are no tasks yet. Create one to start the backlog."
          action={
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setQuickOpen(true)}>
              Create Task
            </Button>
          }
        />
      ) : (
        <Table
          columns={columns}
          rows={filtered}
          rowKey={(t) => t.id}
          onRowClick={(t) => navigate(`/tasks/${t.id}`)}
          empty="No tasks match these filters."
        />
      )}

      <QuickCreateTaskModal
        open={quickOpen}
        onClose={() => setQuickOpen(false)}
        onCreate={create}
        onCreated={() => void refresh()}
        onResearch={researchTask}
        currentSprint={currentSprint}
      />
    </div>
  );
}

export default BacklogPage;
